import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Send, Mic, MicOff, Volume2, VolumeX, Loader2, CheckCircle, Shield, Clock, User } from "lucide-react";
import { toast } from "sonner";

export default function ExamModePage() {
  const navigate = useNavigate();
  const [personas, setPersonas] = useState({});
  const [selectedPersona, setSelectedPersona] = useState("auto");
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [currentPhase, setCurrentPhase] = useState(null);
  const [phaseIndex, setPhaseIndex] = useState(0);
  const [personaInfo, setPersonaInfo] = useState(null);
  const [status, setStatus] = useState("setup");
  const [sending, setSending] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [ttsEnabled, setTtsEnabled] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);

  useEffect(() => {
    api.get("/config/personas").then(r => setPersonas(r.data)).catch(() => {});
  }, []);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  useEffect(() => {
    if (!ttsEnabled || messages.length === 0) return;
    const last = messages[messages.length - 1];
    if (last.role === "assistant") {
      speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(last.content);
      u.lang = "de-DE"; u.rate = 0.85; u.pitch = 0.95;
      const voices = speechSynthesis.getVoices();
      const deVoice = voices.find(v => v.lang.startsWith("de") && v.name.includes("Google")) || voices.find(v => v.lang.startsWith("de"));
      if (deVoice) u.voice = deVoice;
      speechSynthesis.speak(u);
    }
  }, [messages, ttsEnabled]);

  const startExam = async () => {
    setStatus("loading");
    try {
      const payload = selectedPersona === "auto" ? {} : { persona_id: selectedPersona };
      const res = await api.post("/exam/sessions", payload);
      setSessionId(res.data.session_id);
      setMessages(res.data.messages || []);
      setPersonaInfo(res.data.persona);
      setCurrentPhase(res.data.current_phase);
      setStatus("active");
      setStartTime(Date.now());
    } catch (err) {
      toast.error(err.response?.data?.detail || "Prüfung konnte nicht gestartet werden");
      setStatus("setup");
    }
  };

  const sendMessage = async () => {
    if (!message.trim() || sending) return;
    const text = message.trim();
    setMessage(""); setSending(true);
    setMessages(prev => [...prev, { role: "user", content: text, timestamp: new Date().toISOString() }]);
    try {
      const res = await api.post(`/exam/sessions/${sessionId}/message`, { content: text });
      setMessages(prev => [...prev, { role: "assistant", content: res.data.ai_message.content, timestamp: res.data.ai_message.timestamp }]);
      if (res.data.current_phase) setCurrentPhase(res.data.current_phase);
      if (res.data.phase_index !== undefined) setPhaseIndex(res.data.phase_index);
    } catch { toast.error("Nachricht konnte nicht gesendet werden"); }
    setSending(false);
  };

  const completeExam = async () => {
    if (messages.filter(m => m.role === "user").length < 6) { toast.error("Führen Sie mindestens 6 Antworten, bevor Sie die Prüfung abschließen"); return; }
    setCompleting(true);
    try {
      const res = await api.post(`/exam/sessions/${sessionId}/complete`);
      toast.success("Prüfung abgeschlossen!");
      navigate(`/analysis/${sessionId}`);
    } catch { toast.error("Fehler beim Abschließen"); }
    setCompleting(false);
  };

  const toggleListening = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Spracherkennung nicht unterstützt"); return; }
    if (isListening) { recognitionRef.current?.stop(); setIsListening(false); return; }
    const r = new SR(); r.lang = "de-DE"; r.continuous = false;
    r.onresult = e => { setMessage(p => p + e.results[0][0].transcript); setIsListening(false); };
    r.onerror = () => setIsListening(false); r.onend = () => setIsListening(false);
    recognitionRef.current = r; r.start(); setIsListening(true);
  };

  const elapsed = startTime ? Math.floor((Date.now() - startTime) / 60000) : 0;

  // Setup
  if (status === "setup") {
    return (
      <div className="page-transition max-w-2xl mx-auto" data-testid="exam-setup">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold tracking-tight">Prüfungsmodus</h1>
          <p className="font-body text-sm text-muted-foreground mt-1">Realistische MPU-Simulation mit professionellem Gutachter</p>
        </div>

        <Card className="border-border/50 mb-6">
          <CardHeader><CardTitle className="font-heading text-lg">Gutachter wählen</CardTitle>
            <CardDescription className="font-body text-sm">Wählen Sie Ihren Gesprächspartner oder lassen Sie das System entscheiden</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Select value={selectedPersona} onValueChange={setSelectedPersona}>
              <SelectTrigger className="h-11 font-body" data-testid="persona-select">
                <SelectValue placeholder="Gutachter wählen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto" className="font-body">Zufällig (empfohlen)</SelectItem>
                {Object.entries(personas).map(([k, p]) => (
                  <SelectItem key={k} value={k} className="font-body">{p.name} - {p.description}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="grid grid-cols-2 gap-3">
              {Object.entries(personas).map(([k, p]) => (
                <div key={k} onClick={() => setSelectedPersona(k)}
                  className={`p-4 rounded-xl border cursor-pointer transition-all duration-200 ${selectedPersona === k ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"}`}
                  data-testid={`persona-card-${k}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold font-body" style={{ background: p.color }}>{p.avatar}</div>
                    <div><p className="font-body text-sm font-medium">{p.name}</p><Badge variant="secondary" className="text-xs font-body">{p.type}</Badge></div>
                  </div>
                  <p className="font-body text-xs text-muted-foreground">{p.description}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="bg-muted/50 rounded-xl p-4 mb-6 flex items-start gap-3">
          <Shield className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-body text-sm font-medium">Prüfungsmodus-Hinweis</p>
            <p className="font-body text-xs text-muted-foreground mt-1">
              Dies ist eine realistische Simulation des psychologischen Teils der MPU. Der KI-Gutachter wird kritisch nachfragen und Ihre Antworten auf Konsistenz prüfen. Dies ist ein Übungswerkzeug - kein echtes Gutachten.
            </p>
          </div>
        </div>

        <Button className="w-full h-12 font-body text-base bg-primary text-primary-foreground hover:bg-primary/90" onClick={startExam} data-testid="start-exam-btn">
          Prüfung starten
        </Button>
      </div>
    );
  }

  if (status === "loading") return <div className="flex flex-col items-center justify-center py-20"><Loader2 className="w-10 h-10 animate-spin text-primary mb-4" /><p className="font-body text-muted-foreground">Prüfung wird vorbereitet...</p></div>;

  // Active Exam
  return (
    <div className="page-transition flex flex-col h-[calc(100vh-8rem)]" data-testid="exam-chat">
      <div className="flex items-center justify-between pb-4 border-b border-border/50 mb-4">
        <div className="flex items-center gap-3">
          {personaInfo && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold font-body" style={{ background: personaInfo.color }}>{personaInfo.avatar}</div>
              <div><p className="font-body text-sm font-medium">{personaInfo.name}</p><Badge variant="secondary" className="text-xs font-body">{personaInfo.type}</Badge></div>
            </div>
          )}
          {currentPhase && <Badge variant="outline" className="font-body text-xs ml-2">{currentPhase.name}</Badge>}
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="font-body text-xs gap-1"><Clock className="w-3 h-3" />{elapsed} Min</Badge>
          <Button variant="ghost" size="sm" onClick={() => setTtsEnabled(!ttsEnabled)} data-testid="exam-tts-toggle">
            {ttsEnabled ? <Volume2 className="w-4 h-4 text-primary" /> : <VolumeX className="w-4 h-4 text-muted-foreground" />}
          </Button>
          <Button variant="outline" size="sm" onClick={completeExam} disabled={completing} className="font-body gap-1 text-xs" data-testid="complete-exam-btn">
            {completing ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />} Prüfung beenden
          </Button>
        </div>
      </div>

      {/* Phase Progress */}
      <div className="flex gap-1 mb-4">
        {["Biographie", "Delikt", "Veränderung", "Prävention"].map((p, i) => (
          <div key={i} className={`flex-1 h-1.5 rounded-full transition-colors ${i <= phaseIndex ? "bg-primary" : "bg-border"}`} />
        ))}
      </div>

      <ScrollArea className="flex-1 pr-4">
        <div className="space-y-4 pb-4">
          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`exam-msg-${i}`}>
              {msg.role === "assistant" && personaInfo && (
                <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold mr-2 mt-1 flex-shrink-0" style={{ background: personaInfo.color }}>{personaInfo.avatar}</div>
              )}
              <div className={`max-w-[75%] p-4 ${msg.role === "user" ? "chat-bubble-user" : "chat-bubble-ai"}`}>
                <p className="font-body text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
              </div>
            </div>
          ))}
          {sending && <div className="flex justify-start"><div className="w-7 h-7 mr-2" /><div className="chat-bubble-ai p-4 flex gap-1.5"><div className="typing-dot" /><div className="typing-dot" /><div className="typing-dot" /></div></div>}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      <div className="pt-4 border-t border-border/50">
        <div className="flex gap-2">
          <Button variant={isListening ? "destructive" : "outline"} size="icon" className={`flex-shrink-0 h-11 w-11 ${isListening ? "voice-recording" : ""}`} onClick={toggleListening} data-testid="exam-voice-btn">
            {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </Button>
          <Input value={message} onChange={e => setMessage(e.target.value.slice(0, 2000))} onKeyDown={e => e.key === "Enter" && !e.shiftKey && sendMessage()} placeholder="Ihre Antwort..." className="h-11 font-body" disabled={sending} maxLength={2000} data-testid="exam-input" />
          <Button onClick={sendMessage} disabled={!message.trim() || sending} className="flex-shrink-0 h-11 w-11 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="exam-send-btn">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
